/**
 * The `highlight` and `textColor` marks (`031-content-format.md` — Allowed marks;
 * `061-editor.md` §1).
 *
 * **Custom, deliberately — not `@tiptap/extension-highlight` / `-color`.** Those are packages
 * not in `020-tech-stack.md` (an ADR, not an `npm install` — see the header of
 * `extensions.ts`), and their multicolor mode stores an arbitrary colour value and renders it
 * as an inline `style` attribute. This app stores a **constrained palette key** instead
 * (`amber`, `green`, …) and renders it as a `data-*` attribute; the actual colour comes from a
 * CSS token in `styles/theme.css`, so it is theme-aware (adapts light↔dark) and no arbitrary
 * value ever enters `page.content` — the schema stays the tight allow-list that `120` relies
 * on as the XSS boundary.
 *
 * The `color` attr is validated against {@link PALETTE} on the way in (`parseHTML`) and out
 * (`renderHTML`): an unknown key renders no attribute, so the mark cannot carry anything but a
 * known palette key even though it exists in the schema.
 */
import { Mark, mergeAttributes } from '@tiptap/core';

/** The palette keys. One set, reused by both marks so the swatch UI is uniform. */
export const PALETTE_COLORS = ['amber', 'green', 'blue', 'purple', 'red', 'gray'] as const;
export type PaletteColor = (typeof PALETTE_COLORS)[number];

const PALETTE = new Set<string>(PALETTE_COLORS);

/** A palette key, or `null` for anything else — the single gate that keeps stray values out. */
export function normalizePaletteColor(value: unknown): PaletteColor | null {
  return typeof value === 'string' && PALETTE.has(value) ? (value as PaletteColor) : null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    highlight: {
      setHighlight: (attrs: { color: PaletteColor }) => ReturnType;
      unsetHighlight: () => ReturnType;
    };
    textColor: {
      setTextColor: (attrs: { color: PaletteColor }) => ReturnType;
      unsetTextColor: () => ReturnType;
    };
  }
}

/** `<mark data-color="amber">` — a background highlight. */
export const Highlight = Mark.create({
  name: 'highlight',

  addAttributes() {
    return {
      color: {
        default: null as PaletteColor | null,
        parseHTML: (element) => normalizePaletteColor(element.getAttribute('data-color')),
        renderHTML: (attributes) => {
          const color = normalizePaletteColor(attributes['color']);
          return color === null ? {} : { 'data-color': color };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'mark[data-color]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['mark', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setHighlight:
        (attrs) =>
        ({ commands }) => {
          if (normalizePaletteColor(attrs.color) === null) return false;
          return commands.setMark(this.name, attrs);
        },
      unsetHighlight:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

/** `<span data-text-color="blue">` — a font colour. */
export const TextColor = Mark.create({
  name: 'textColor',

  addAttributes() {
    return {
      color: {
        default: null as PaletteColor | null,
        parseHTML: (element) => normalizePaletteColor(element.getAttribute('data-text-color')),
        renderHTML: (attributes) => {
          const color = normalizePaletteColor(attributes['color']);
          return color === null ? {} : { 'data-text-color': color };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-text-color]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setTextColor:
        (attrs) =>
        ({ commands }) => {
          if (normalizePaletteColor(attrs.color) === null) return false;
          return commands.setMark(this.name, attrs);
        },
      unsetTextColor:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});
